import { inject, Injectable } from '@angular/core';

import { KeyValueType } from '@models/common';
import { GenericObjectType } from '@models/helpers';
import { BaseRestrictions } from '@models/module/fields/enums';
import { SearchViewModel } from '@models/module/grid';
import { Permission } from '@models/permission';
import {
  ScheduledEmail,
  ScheduledEmailAction,
  ScheduledEmailActionsEnum,
  ScheduledEmailAdditionalRoles,
  ScheduledEmailRow,
  ScheduledEmailsResponse,
  ScheduleFrequencyEnum,
} from '@module-instance/scheduled-emails/interfaces';
import { DataListCommandFilterType, DisplayFormats, TableColumn } from '@shared/components/data-list/common';
import { HttpService } from '@shared/services';

@Injectable({ providedIn: 'root' })
export class ScheduledEmailService {
  private http = inject(HttpService);

  getScheduledEmails(search: SearchViewModel) {
    return this.http.post<ScheduledEmailsResponse>('_api/jobs/scheduled/all', search, {
      useLoader: false,
    });
  }

  getModulesDropdownItems(minAgencyDays = 0) {
    const params = this.http.buildHttpParams({ minAgencyDays });
    return this.http.get<KeyValueType<number>[]>(`_api/ModuleDefinition/GetNonContractModulesDict`, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getAdditionalRoles() {
    const params = this.http.buildHttpParams({ includeSystemRoles: false });
    return this.http.get<ScheduledEmailAdditionalRoles[]>('_api/role/dict', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  createScheduledEmail(data: ScheduledEmail) {
    return this.http.post<ScheduledEmail>('_api/jobs/scheduled', data, { useLoader: false });
  }

  getPlaceholders() {
    return this.http.get<GenericObjectType>('_api/placeholders/email/all', { useLoader: false });
  }

  updateScheduledEmail(data: ScheduledEmail) {
    return this.http.put<ScheduledEmail>(`_api/jobs/scheduled/${data.id}`, data, {
      useLoader: false,
    });
  }

  toggleScheduled(jobId: number, state: boolean) {
    return this.http.put<ScheduledEmail>(`_api/jobs/scheduled/${jobId}/status`, state, {
      useLoader: false,
    });
  }

  deleteScheduledEmail(jobId: number) {
    return this.http.delete(`_api/jobs/scheduled/${jobId}`, { useLoader: false });
  }

  getTableColumns(): TableColumn[] {
    return [
      {
        id: 0,
        name: 'jobName',
        displayName: 'general.table.name',
        width: 2,
        sortable: true,
        priority: 3,
      },
      {
        id: 1,
        name: 'description',
        displayName: 'general.table.description',
        width: 3,
        sortable: true,
        priority: 0,
      },
      {
        id: 2,
        name: 'scheduleFrequency',
        displayName: 'SCHEDULED_EMAILS.frequency.label',
        width: 1,
        sortable: true,
        priority: 2,
      },
      {
        id: 3,
        name: 'timeOfDay',
        displayName: 'general.table.time',
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.Time,
        width: 2,
        sortable: true,
        priority: 0,
      },
      {
        id: 4,
        name: 'createdByFullName',
        displayName: 'general.table.createdBy',
        width: 2,
        sortable: true,
        priority: 0,
      },
      {
        id: 5,
        name: 'updatedOn',
        displayName: 'general.table.updatedOn',
        displayFormatId: DisplayFormats.Date,
        restrictions: BaseRestrictions.DateTime,
        width: 2,
        sortable: true,
        priority: 0,
      },
      {
        id: 6,
        name: 'isEnabled',
        displayName: 'general.table.active',
        displayFormatId: DisplayFormats.YesNo,
        width: 1,
        sortable: true,
        priority: 3,
      },
      {
        id: 7,
        name: 'lastRunHasError',
        displayName: 'SCHEDULED_EMAILS.lastRunHasError',
        displayFormatId: DisplayFormats.YesNo,
        width: 1,
        sortable: true,
        priority: 3,
      },
    ];
  }

  getTableActions(row: ScheduledEmailRow, permissions: Permission[]): ScheduledEmailAction[] {
    return [
      permissions.includes(Permission.Update) && {
        id: ScheduledEmailActionsEnum.SEND_NOW,
        displayName: 'actions.sendNow',
        leftIcon: 'edit',
        condition: row => row.scheduleFrequency === ScheduleFrequencyEnum.MANUAL,
      },
      {
        id: ScheduledEmailActionsEnum.EDIT,
        displayName: permissions.includes(Permission.Update) ? 'actions.edit' : 'actions.view',
        leftIcon: permissions.includes(Permission.Update) ? 'edit' : 'description',
      },
      permissions.includes(Permission.Update) && {
        id: ScheduledEmailActionsEnum.TOGGLE,
        displayName: row.isEnabled === true ? 'actions.deactivate' : 'actions.activate',
        leftIcon: 'toggle_on',
      },
      permissions.includes(Permission.Delete) && {
        id: ScheduledEmailActionsEnum.DELETE,
        displayName: 'actions.delete',
        leftIcon: 'delete',
      },
    ].filter(a => !!a);
  }

  getFilters(): DataListCommandFilterType[] {
    return [
      {
        fieldId: 'jobName',
        name: 'general.table.name',
        operators: ['Contains', 'DoesNotContain', 'EndsWith', 'StartsWith'],
        type: 'text',
      },
      {
        fieldId: 'description',
        name: 'general.table.description',
        operators: ['Contains', 'DoesNotContain', 'EndsWith', 'StartsWith'],
        type: 'text',
      },
      {
        fieldId: 'scheduleFrequency',
        name: 'SCHEDULED_EMAILS.frequency.label',
        operators: ['Equal', 'NotEqual'],
        selectedOperator: 'Equal',
        values: [
          { key: '1', value: 'SCHEDULED_EMAILS.frequency.DAILY' },
          { key: '2', value: 'SCHEDULED_EMAILS.frequency.WEEKLY' },
          // disable manual for now
          //{ key: '3', value: 'SCHEDULED_EMAILS.frequency.MANUAL' },
        ],
        type: 'number',
      },
      {
        fieldId: 'updatedOn',
        name: 'general.table.updatedOn',
        type: 'date',
      },
      {
        fieldId: 'isEnabled',
        name: 'general.table.active',
        values: [
          { key: 'true', value: 'general.yes' },
          { key: 'false', value: 'general.no' },
        ],
        type: 'bool',
      },
      {
        fieldId: 'lastRunHasError',
        name: 'SCHEDULED_EMAILS.lastRunHasError',
        values: [
          { key: 'true', value: 'general.yes' },
          { key: 'false', value: 'general.no' },
        ],
        type: 'bool',
      },
    ];
  }
}
